import { FC, useState } from 'react'
import { Checkbox, Popover, Select, Tag } from 'antd'
import {
  ArrowUpOutlined,
  BorderOutlined,
  BulbOutlined,
  ToolOutlined,
} from '@ant-design/icons'
import { cn } from '@/lib/utils'

/** doc 104:composer 工具项(选中数量 + Popover 勾选,替代配置卡片里的常驻列表) */
export interface ComposerTool {
  toolName: string
  executionLocation: string
}

/** doc 109:composer 模板项(选择器挪到输入框 toolbar,思考开关前) */
export interface ComposerTemplate {
  templateId: string
  name: string
}

/** doc 117:composer connector 选择项(模板选择器后面,默认空=走模板/系统默认) */
export interface ComposerConnector {
  connectorId: string
  connectorName: string
}

/** 现代 AI 聊天 composer:圆角容器 + toolbar(模板/连接器/思考/工具)+ 发送/停止变体按钮。 */
export const ChatComposer: FC<{
  disabled: boolean
  running: boolean
  templates: ComposerTemplate[]
  templateId: string
  onTemplateChange: (id: string) => void
  // doc 117:connector 自由切换(空=走模板/系统默认;粘性 override,切模板不重置)
  connectors: ComposerConnector[]
  connectorId: string
  onConnectorChange: (id: string) => void
  /** 跟随模板位显示的实际 connector 名(模板未配 → 空串 → 显示「系统默认」) */
  templateConnectorName?: string
  thinking: boolean
  onThinkingChange: (v: boolean) => void
  tools: ComposerTool[]
  selectedTools: string[]
  onToolsChange: (names: string[]) => void
  onSend: (text: string) => void
  onStop: () => void
}> = ({
  disabled,
  running,
  templates,
  templateId,
  onTemplateChange,
  connectors,
  connectorId,
  onConnectorChange,
  templateConnectorName = '',
  thinking,
  onThinkingChange,
  tools,
  selectedTools,
  onToolsChange,
  onSend,
  onStop,
}) => {
  const [val, setVal] = useState('')

  const submit = () => {
    if (val.trim() && !disabled && !running) {
      onSend(val)
      setVal('')
    }
  }

  const canSend = !disabled && !running && !!val.trim()

  const toggleTool = (name: string, checked: boolean) => {
    onToolsChange(
      checked ? [...selectedTools, name] : selectedTools.filter(n => n !== name),
    )
  }

  const toolsContent = (
    <div className="flex max-h-64 w-60 flex-col gap-0.5 overflow-y-auto">
      {tools.length === 0 && (
        <div className="py-2 text-center text-xs text-muted-foreground">暂无注册工具</div>
      )}
      {tools.map(t => (
        <label
          key={t.toolName}
          className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-muted"
        >
          <Checkbox
            checked={selectedTools.includes(t.toolName)}
            onChange={e => toggleTool(t.toolName, e.target.checked)}
          />
          <span className="min-w-0 flex-1 truncate text-xs" title={t.toolName}>
            {t.toolName}
          </span>
          <span
            className={cn(
              'shrink-0 rounded-full border px-1.5 text-[10px] font-medium leading-4',
              t.executionLocation === 'agent'
                ? 'border-blue-200 bg-blue-50 text-blue-600'
                : 'border-purple-200 bg-purple-50 text-purple-600',
            )}
          >
            {t.executionLocation}
          </span>
        </label>
      ))}
    </div>
  )

  return (
    <div
      className={cn(
        'mt-3 rounded-2xl border bg-background shadow-sm transition-colors focus-within:border-primary',
        disabled && !running && 'opacity-70',
      )}
    >
      <textarea
        value={val}
        onChange={e => setVal(e.target.value)}
        placeholder={
          running ? '运行中,可继续输入…' : disabled ? '等待工具执行…' : '输入消息…'
        }
        rows={2}
        className="w-full resize-none rounded-t-2xl bg-transparent px-3.5 pt-3 text-sm outline-none placeholder:text-muted-foreground"
        onKeyDown={e => {
          // isComposing:中文输入法组词期间的 Enter 不触发发送
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault()
            submit()
          }
        }}
      />
      <div className="flex items-center gap-1.5 px-3 pb-2">
        {/* doc 109:模板选择器 — borderless 小尺寸,与 chip 高度对齐 */}
        <Select
          variant="borderless"
          size="small"
          value={templateId || undefined}
          onChange={onTemplateChange}
          options={templates.map(t => ({ value: t.templateId, label: t.name }))}
          placeholder="选择模板"
          popupMatchSelectWidth={false}
          style={{ maxWidth: 140 }}
          className="[&_.ant-select-selection-wrap]:text-xs"
        />
        {/* doc 117:连接器选择器 — 模板后面;首项「跟随模板」显示实际 connector(模板未配 → 系统默认),override 时高亮 primary */}
        <Select
          variant="borderless"
          size="small"
          value={connectorId || ''}
          onChange={(v: string) => onConnectorChange(v || '')}
          options={[
            {
              value: '',
              label: (
                <span className="flex items-center gap-1">
                  <Tag
                    color={templateConnectorName ? 'blue' : 'default'}
                    className="mr-0 px-1 text-[10px] leading-4"
                  >
                    {templateConnectorName ? '模板' : '默认'}
                  </Tag>
                  {templateConnectorName || '系统默认'}
                </span>
              ),
            },
            ...connectors.map(c => ({ value: c.connectorId, label: c.connectorName })),
          ]}
          placeholder="连接器"
          popupMatchSelectWidth={false}
          style={{ maxWidth: 160 }}
          className={cn(
            '[&_.ant-select-selection-wrap]:text-xs',
            connectorId && '[&_.ant-select-selection-wrap]:font-medium [&_.ant-select-selection-wrap]:text-primary',
          )}
        />
        {/* doc 104:思考开关 — 点亮=启用思考 */}
        <button
          type="button"
          onClick={() => onThinkingChange(!thinking)}
          title={thinking ? '思考已启用,点击禁用' : '思考已禁用,点击启用'}
          className={cn(
            'flex h-7 items-center gap-1 rounded-full border px-2.5 text-xs transition-colors',
            thinking
              ? 'border-primary/40 bg-primary/5 text-primary'
              : 'border-border text-muted-foreground hover:border-primary/30',
          )}
        >
          <BulbOutlined style={{ fontSize: 12 }} />
          思考
        </button>
        {/* doc 104:工具选择器 — Popover 收起,徽标显已选数 */}
        <Popover content={toolsContent} trigger="click" placement="topLeft">
          <button
            type="button"
            className="flex h-7 items-center gap-1 rounded-full border border-border px-2.5 text-xs text-muted-foreground transition-colors hover:border-primary/30"
          >
            <ToolOutlined style={{ fontSize: 12 }} />
            工具 · {selectedTools.length}
          </button>
        </Popover>
        {!running && <span className="flex-1 sm:hidden" />}
        {/* doc 104:发送/停止变体 — 同一按钮位,running 时变红色停止 */}
        {running ? (
          <button
            type="button"
            onClick={onStop}
            aria-label="停止"
            title="停止生成"
            className="ml-auto flex size-7 items-center justify-center rounded-full bg-red-500 text-white transition-colors hover:bg-red-600"
          >
            <BorderOutlined style={{ fontSize: 11 }} />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={!canSend}
            aria-label="发送"
            className={cn(
              'ml-auto flex size-7 items-center justify-center rounded-full transition-colors',
              canSend
                ? 'bg-primary text-primary-foreground hover:opacity-90'
                : 'bg-muted text-muted-foreground',
            )}
          >
            <ArrowUpOutlined style={{ fontSize: 13 }} />
          </button>
        )}
      </div>
    </div>
  )
}
